/**
 * Created by Ning Jiang on 2021-06-07.
 */

import {WinPresentationController} from "../WinPresentationController";
import {ReelState} from "../../../core/reel/ReelState";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Karen} from "../../Karen";
import {TimelineLite} from "gsap";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {KarenSymbol} from "../../symbol/KarenSymbol";
import {SpineAnimation} from "../../view/SpineAnimation";
import {KarenSoundManager} from "../../sound/KarenSoundManager";
import {KarenSoundConfig} from "../../sound/KarenSoundConfig";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {GameHelper} from "../../../core/utils/GameHelper";
import {KarenSymbolState} from "../../symbol/KarenSymbolConfig";

export class WildNudgeController extends WinPresentationController {

    private readonly _nudgeSymbol: string = "W";
    private _nudgeStepDuration: number = 0.4;
    private readonly _nudgeEase: string = `elastic.out(1.1, 1)`;
    private _steps: number;
    private _wildIndex: number;
    private _wildLandedOnReel: number;
    private _isFastPlay: boolean = false;

    constructor() {
        super("WildNudge", true);
    }

    protected hasWin(): boolean {
        const reels: string[][] = Karen.model.parsedGameData.reels!;
        this._wildIndex = reels[5].indexOf(this._nudgeSymbol, 1);
        this._wildLandedOnReel = this._wildIndex;
        return ( this._wildIndex > -1 && this._wildIndex < reels![5].length );
    }

    // Please NOTE: if no win, the data can complete before the actual animation is rendered. So eg, if you update the xNudge wild multiplier on step start/complete,
    // you might get the wp complete before the actual animation because the framerate is bigger than the animation duration.
    // So you have to make sure everything is really complete in onReelNudgeComplete() so you don't get a confused state in the next wp.
    protected async createWinAnimation(): Promise<void> {
        this._isFastPlay = !Karen.model.parsedGameData.isWildParticipatingInWin || Karen.apiPlugin.settings.get(APISetting.FAST_SPIN);
        const isFSMode: boolean = Karen.model.parsedGameData.mode === GameMode.FREESPIN;
        const toPlayNudgeinFS: boolean = (isFSMode && !Karen.model.parsedGameData.isWildParticipatingInWin);
        const hasWin: boolean = toPlayNudgeinFS || Karen.model.parsedGameData.isWildParticipatingInWin;
        let delay: number = 0.01;
        const promises: Promise<void>[] = [];
        const reelId: number = 5;
        this._nudgeStepDuration = this._isFastPlay ? 0.05 : this._nudgeStepDuration;
        this._steps = Karen.model.parsedGameData.evaluatedArea[reelId].length - this._wildIndex;
        const multiplierVal: number = 2;
        const promise: Promise<void> = new Promise<void>(resolve => {
                Karen.reels[reelId].nudge(
                    this._steps,
                    this._wildIndex - 1,
                    multiplierVal,
                    hasWin ? this._nudgeStepDuration : 0.01,
                    this._nudgeEase,
                    delay,
                    (stepsLeft: number, aborted: boolean) => this.onNudgeStepStart(reelId, stepsLeft, aborted, this._wildIndex),
                    (stepsLeft: number, aborted: boolean) => this.onNudgeStepComplete(reelId, stepsLeft, this._wildIndex, aborted),
                    async (aborted: boolean) => {
                        await this.onReelNudgeComplete(reelId, aborted, resolve);
                    },
                    false
                );
            }
        );
        promises.push(promise);

        await Promise.all(promises);
    }

    protected skip(): void {
        let playAbortSound: boolean = false;
        Karen.reels.forEach(reel => {
            if (reel.reelState === ReelState.NUDGE) {
                reel.abortNudge();
                playAbortSound = true;
            }
        });
        if (playAbortSound) {
            // playAbortSound();
        }
    }

    // The range is the startIndex (include) in the reel symbols to the endIndex (NOT include).
    // For a 3x5 slot, there are 3 symbols in reel, then the range is [0, 3],

    protected getNextReelNudgeDelayDelta(reelId: number): number {
        return Math.abs(this._steps) * this._nudgeStepDuration;
    }

    private onNudgeStepStart(reelId: number, stepsLeft: number, aborted: boolean, wildIndex: number): void {
        if (aborted) {
            if ((wildIndex - this._wildLandedOnReel) === 0) {
                if (Karen.model.parsedGameData.isWildParticipatingInWin) {
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.xNudgeNudgeFastWin);
                } else {
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.xNudgeNudgeFastNoWin);
                }
            }
        } else {
            KarenSoundManager.playSoundEffect(KarenSoundConfig.xNudgesSounds[wildIndex - this._wildLandedOnReel]);
        }
        const wildSym: KarenSymbol = Karen.reels[5].reelView.allSymbols[wildIndex + 1];
        const stagePos: PIXI.Point = wildSym.getStagePosition();
        const wildAn: SpineAnimation = new SpineAnimation("wild");
        wildAn.setStagePosition(stagePos);
        this._isFastPlay = !Karen.model.parsedGameData.isWildParticipatingInWin|| Karen.apiPlugin.settings.get(APISetting.FAST_SPIN);
        let duration: number = this._isFastPlay ? 0.1 : wildAn.getAnimDuration("nudge");
        !aborted && wildAn.playSpineAnimation("nudge", aborted, duration);
        Logger.logDev(`Reel ${reelId} start nudge step, stepsLeft = ${stepsLeft}, abort = ${aborted}`);
    }

    private onNudgeStepComplete(reelId: number, multiplierVal: number, wildIndex: number, aborted: boolean): void {
        this._wildIndex = wildIndex + 1;
        this.increaseMultiplier(Karen.reels[5].symbolNumberOnTop, multiplierVal, wildIndex);
        //Karen.background.incrementxWayBy1();
        Logger.logDev(`Reel ${reelId} completed nudge step, stepsLeft = ${multiplierVal}, abort = ${aborted}`);
    }

    private onReelNudgeComplete(reelId: number, aborted: boolean, resolve: () => void): void {
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                resolve();
                if (Karen.model.parsedGameData.mode == GameMode.FREESPIN) {
                    //SlotGame.keypadPlugin.setZeroBetSpinCounter(Karen.model.parsedGameData.freespinsLeft);
                }
            }
        });
        Logger.logDev(`Reel ${reelId} completed reel nudge, abort = ${aborted}`);
        const startIndex: number = Karen.reels[5].symbolNumberOnTop;
        let multiplierVal: number = Karen.model.parsedGameData.symbolSizes[5][0];
        if (Karen.model.parsedGameData.xWInfPositions[5].length > 0) {
            for (let symIndx: number = 0; symIndx < Karen.model.parsedGameData.evaluatedArea[5].length; symIndx++) {
                for (let symId: number = 0; symId < Karen.model.parsedGameData.xWInfPositions[5].length; symId++) {
                    if (Karen.model.parsedGameData.xWInfPositions[reelId][symId] !== symIndx) {
                        multiplierVal = Karen.model.parsedGameData.symbolSizes[5][symIndx];//should nott be infectious
                        break;
                    }
                }
            }
        }
        if(!GameHelper.isXWaysPresent(Karen.model.parsedGameData)){
            Karen.xwaysMultiplierView.setXWays(Karen.model.parsedGameData.xWayNumbersOnEachNudgeStep.toString());
        }
        const wildSym: KarenSymbol = Karen.reels[5].reelView.allSymbols[this._wildIndex];
        const timePos: number = aborted ? 0.01 : 1;
        let showMultiplier: boolean = false;
        if (GameHelper.isFreeSpinTrigger(Karen.model.parsedGameData.mode, Karen.model.parsedGameData.nextMode)) {
            showMultiplier = true;
        }
        if (GameHelper.isFreeSpin(Karen.model.parsedGameData.mode)) {
            showMultiplier = true;
        }
        if (wildSym.symbolName === this._nudgeSymbol && !aborted) {
            tl.add([
                this.playBombExplodeAnimation(wildSym, aborted),
                wildSym.getStateAnimation(KarenSymbolState.END,{duration: 0, alpha: 1}, {duration: 0})
            ], 0);
        }else{
            tl.add(wildSym.getStateAnimation(KarenSymbolState.NO_WIN_END,{duration: 0, alpha: 1}, {duration: 0}));
        }
        if (showMultiplier || multiplierVal > 1) {
            let count: number = 0;
            let length: number = Karen.model.parsedGameData.evaluatedArea[5].length - 1;
            for (let symIndex: number = length; symIndex > -1; symIndex--) {
                count++;
                tl.add([Karen.reels[5].reelView.allSymbols[startIndex + symIndex].revealXWMultiplier(multiplierVal),
                ()=>{
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.MULTIPLIER_STAMP.next());
                }], timePos + (0.05 * count));
            }
            Karen.background.setXWayValue(multiplierVal);
        }
        tl.to(this, 0.01, {})
    }

    private playBombExplodeAnimation(wildSym: KarenSymbol, abort: boolean): TimelineLite {
        const reelSize: number = Karen.model.parsedGameData.evaluatedArea[5].length;
        const tl: TimelineLite = new TimelineLite();
        const stagePos: PIXI.Point = wildSym.getStagePosition();
        const wildAn: SpineAnimation = new SpineAnimation("wild");
        wildAn.setStagePosition(stagePos);
        let duration: number = abort ? 0.2 : wildAn.getAnimDuration("trigger_" + reelSize);
        tl.add(wildAn.playSpineAnimation("trigger_" + reelSize, abort, duration));
        tl.add(() => {
            KarenSoundManager.playSoundEffect(KarenSoundConfig.xNudgeExplode, 0, 1);
        }, 0);
        return tl;

    }

    private increaseMultiplier(startIndex: number, multiplierVal: number, wildIndex: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const wildSym: KarenSymbol = Karen.reels[5].reelView.allSymbols[wildIndex + 1];
        if (wildSym.symbolName === this._nudgeSymbol) {
            tl.add([wildSym.revealMultiplier(multiplierVal),
                ()=>{
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.MULTIPLIER_STAMP.next());
                }], 0);
        }
        return tl;
    }
}
